import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';

// CSS to hide number input spinners
const hideNumberInputSpinners = {
  '& input[type=number]': {
    MozAppearance: 'textfield',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
  },
};

// Available languages
const LANGUAGES = [
  { code: 'ja-JP', name: 'Japanese' },
  { code: 'en-US', name: 'English' },
  { code: 'id-ID', name: 'Indonesian' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
];

// Available rule types
const RULE_TYPES = [
  { value: 'priority', label: 'Priority' },
  { value: 'count', label: 'Count' },
];

// Empty special rule template
const EMPTY_RULE = {
  type: 'priority',
  condition: '',
  actions: {
    extraFee: '',
    extraWage: '',
  },
  remark: '',
};

// Helper function to safely check if element is an input element
const isHTMLInputElement = (element) => {
  return element && typeof element.select === 'function';
};

const CreateTagModal = ({ isOpen, onClose, organizationId, onTagCreated }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // New state for additional fields
  const [tagName, setTagName] = useState('');
  const [tagDescription, setTagDescription] = useState('');
  const [tagDisplayNames, setTagDisplayNames] = useState([
    { langCode: 'ja-JP', name: '' },
  ]);
  const [defaultFee, setDefaultFee] = useState(0);
  const [defaultWage, setDefaultWage] = useState(0);
  const [specialRules, setSpecialRules] = useState([]);

  // Reset form state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setTagName('');
      setTagDescription('');
      setTagDisplayNames([{ langCode: 'ja-JP', name: '' }]);
      setDefaultFee(0);
      setDefaultWage(0);
      setSpecialRules([]);
      setError(null);
      setSuccess(false);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Handle language display name changes
  const handleTagDisplayNameChange = (index, field, value) => {
    const newTagDisplayNames = [...tagDisplayNames];
    newTagDisplayNames[index][field] = value;
    setTagDisplayNames(newTagDisplayNames);
  };

  // Add a new language field
  const addLanguageField = () => {
    // Find an unused language
    const usedLanguages = tagDisplayNames.map((item) => item.langCode);
    const availableLanguage = LANGUAGES.find(
      (lang) => !usedLanguages.includes(lang.code)
    );

    if (availableLanguage) {
      setTagDisplayNames([
        ...tagDisplayNames,
        { langCode: availableLanguage.code, name: '' },
      ]);
    }
  };

  // Remove a language field
  const removeLanguageField = (index) => {
    if (tagDisplayNames.length > 1) {
      const newTagDisplayNames = [...tagDisplayNames];
      newTagDisplayNames.splice(index, 1);
      setTagDisplayNames(newTagDisplayNames);
    }
  };

  // Add a new special rule
  const addSpecialRule = () => {
    setSpecialRules([...specialRules, { ...EMPTY_RULE }]);
  };

  // Remove a special rule
  const removeSpecialRule = (index) => {
    const newRules = [...specialRules];
    newRules.splice(index, 1);
    setSpecialRules(newRules);
  };

  // Handle special rule changes
  const handleRuleChange = (index, field, value) => {
    const newRules = [...specialRules];

    if (field === 'extraFee' || field === 'extraWage') {
      // If the value is empty or just a minus sign, keep it as is for now
      // This allows users to type negative numbers
      if (value === '' || value === '-') {
        newRules[index].actions[field] = value;
      } else {
        newRules[index].actions[field] = Number(value);
      }
    } else {
      newRules[index][field] = value;
    }

    setSpecialRules(newRules);
  };

  // Handle input changes for numeric fields
  const handleNumericChange = (setter, value) => {
    // If the value is empty or just a minus sign, allow it (for starting negative numbers)
    if (value === '' || value === '-') {
      setter(value);
    } else {
      setter(Number(value));
    }
  };

  // Get condition placeholder text based on rule type
  const getConditionPlaceholder = (type) => {
    switch (type) {
      case 'priority':
        return 'e.g. priority === 3';
      case 'count':
        return 'e.g. count > 0 && count < 100';
      default:
        return '';
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!tagName.trim()) {
      setError('Tag name is required');
      return;
    }

    // Validate first language display name
    if (!tagDisplayNames[0]?.name.trim()) {
      setError('Display name for primary language is required');
      return;
    }

    // Special rules validation
    if (specialRules.length > 0) {
      for (let i = 0; i < specialRules.length; i++) {
        const rule = specialRules[i];
        if (!rule.condition.trim()) {
          setError(`Condition is required for Rule #${i + 1}`);
          return;
        }
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Prepare tag data with label texts in different languages
      const tagDisplayNamesDict = {};
      tagDisplayNames.forEach((item) => {
        if (item.name.trim()) {
          tagDisplayNamesDict[item.langCode] = item.name.trim();
        }
      });

      // Single tag item to be created
      const tag = {
        name: tagName.trim(),
        displayName: tagDisplayNamesDict,
        description: tagDescription.trim() || undefined,
        defaultFee: Number(defaultFee),
        defaultWage: Number(defaultWage),
        specialRules: specialRules.length > 0 ? specialRules : undefined,
      };

      // API call to create a new tag
      const response = await api.post(DOLPHIN_API.ADMIN_TAGS, {
        data: {
          organizationId,
          tag,
        },
      });

      setSuccess(true);

      // Notify parent component about the new tag
      if (onTagCreated) {
        onTagCreated(response);
      }

      // Close the modal after successful creation
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating tag:', err);
      setError(err.message || 'Failed to create tag. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render the form
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper">
      <DialogTitle>
        Create New Tag
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Tag created successfully!
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Tag Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Basic Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <TextField
                  autoFocus
                  margin="dense"
                  id="tag-name"
                  label="Tag Name"
                  placeholder="Enter a unique identifier for this tag"
                  helperText="This is the internal identifier for the tag"
                  type="text"
                  fullWidth
                  required
                  value={tagName}
                  onChange={(e) => setTagName(e.target.value)}
                  disabled={isSubmitting || success}
                  sx={{ mb: 2 }}
                />

                <TextField
                  margin="dense"
                  id="tag-description"
                  label="Description (Optional)"
                  placeholder="Add details about this tag for admin reference"
                  type="text"
                  fullWidth
                  multiline
                  rows={2}
                  value={tagDescription}
                  onChange={(e) => setTagDescription(e.target.value)}
                  disabled={isSubmitting || success}
                  sx={{ mb: 2 }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Fee"
                      type="number"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">￥</InputAdornment>
                          ),
                        },
                        htmlInput: {
                          min: 0, // Ensure `min` is passed to the underlying HTML <input>
                        },
                      }}
                      value={defaultFee}
                      onChange={(e) =>
                        handleNumericChange(setDefaultFee, e.target.value)
                      }
                      onClick={(e) => {
                        if (isHTMLInputElement(e.target)) {
                          e.target.select();
                        }
                      }}
                      sx={hideNumberInputSpinners}
                      required
                      disabled={isSubmitting || success}
                      helperText="Standard fee charged to sender organization"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Default Wage"
                      type="number"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">￥</InputAdornment>
                          ),
                        },
                        htmlInput: {
                          min: 0, // Ensure `min` is passed to the underlying HTML <input>
                        },
                      }}
                      value={defaultWage}
                      onChange={(e) =>
                        handleNumericChange(setDefaultWage, e.target.value)
                      }
                      onClick={(e) => {
                        if (isHTMLInputElement(e.target)) {
                          e.target.select();
                        }
                      }}
                      sx={hideNumberInputSpinners}
                      required
                      disabled={isSubmitting || success}
                      helperText="Standard wage paid to doctors"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Display Names in Different Languages */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Display Names
                </Typography>
                {tagDisplayNames.length < LANGUAGES.length && (
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={addLanguageField}
                    disabled={isSubmitting || success}>
                    Add Language
                  </Button>
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add names that will be displayed to users in different languages
              </Typography>

              {tagDisplayNames.map((item, index) => (
                <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                  <Grid item xs={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`language-select-${index}`}>
                        Language
                      </InputLabel>
                      <Select
                        labelId={`language-select-${index}`}
                        value={item.langCode}
                        label="Language"
                        onChange={(e) =>
                          handleTagDisplayNameChange(
                            index,
                            'langCode',
                            e.target.value
                          )
                        }
                        disabled={isSubmitting || success}>
                        {LANGUAGES.map((lang) => (
                          <MenuItem
                            key={lang.code}
                            value={lang.code}
                            disabled={tagDisplayNames.some(
                              (d, i) => i !== index && d.langCode === lang.code
                            )}>
                            {lang.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={8}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Display Name"
                      placeholder={`Enter name in ${
                        LANGUAGES.find((l) => l.code === item.langCode)?.name
                      }`}
                      value={item.name}
                      onChange={(e) =>
                        handleTagDisplayNameChange(
                          index,
                          'name',
                          e.target.value
                        )
                      }
                      required={index === 0}
                      error={index === 0 && !item.name.trim() && error}
                      helperText={
                        index === 0 && !item.name.trim() && error
                          ? 'Display name is required for primary language'
                          : ''
                      }
                      disabled={isSubmitting || success}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={1}
                    sx={{ display: 'flex', alignItems: 'center' }}>
                    {tagDisplayNames.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => removeLanguageField(index)}
                        disabled={isSubmitting || success}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {/* Special Rules Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}>
                <Typography variant="subtitle1" fontWeight="medium">
                  Special Rules
                </Typography>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={addSpecialRule}
                  disabled={isSubmitting || success}>
                  Add Rule
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add special pricing rules based on conditions
              </Typography>

              {specialRules.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2, textAlign: 'center' }}>
                  No special rules added yet. Click &quot;Add Rule&quot; to
                  create one.
                </Typography>
              ) : (
                specialRules.map((rule, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}>
                        <Chip
                          label={`Rule #${index + 1}`}
                          color="primary"
                          size="small"
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeSpecialRule(index)}
                          disabled={isSubmitting || success}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Grid>

                      {/* First row: Rule Type and Condition with 3:7 ratio */}
                      <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Rule Type</InputLabel>
                          <Select
                            value={rule.type}
                            label="Rule Type"
                            onChange={(e) =>
                              handleRuleChange(index, 'type', e.target.value)
                            }
                            disabled={isSubmitting || success}>
                            {RULE_TYPES.map((type) => (
                              <MenuItem key={type.value} value={type.value}>
                                {type.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={9}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Condition"
                          placeholder={getConditionPlaceholder(rule.type)}
                          value={rule.condition}
                          onChange={(e) =>
                            handleRuleChange(index, 'condition', e.target.value)
                          }
                          disabled={isSubmitting || success}
                          required
                          helperText="JavaScript-like condition to evaluate"
                        />
                      </Grid>

                      {/* Second row: Extra Fee and Extra Wage */}
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Extra Fee"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  ￥
                                </InputAdornment>
                              ),
                            },
                            htmlInput: {
                              min: 0, // Ensure `min` is passed to the underlying HTML <input>
                            },
                          }}
                          value={rule.actions.extraFee}
                          onChange={(e) =>
                            handleRuleChange(index, 'extraFee', e.target.value)
                          }
                          onClick={(e) => {
                            if (isHTMLInputElement(e.target)) {
                              e.target.select();
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          sx={hideNumberInputSpinners}
                          required
                          disabled={isSubmitting || success}
                          helperText="Additional fee when this rule applies"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Extra Wage"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  ￥
                                </InputAdornment>
                              ),
                            },
                            htmlInput: {
                              min: 0, // Ensure `min` is passed to the underlying HTML <input>
                            },
                          }}
                          value={rule.actions.extraWage}
                          onChange={(e) =>
                            handleRuleChange(index, 'extraWage', e.target.value)
                          }
                          onClick={(e) => {
                            if (isHTMLInputElement(e.target)) {
                              e.target.select();
                            }
                          }}
                          onFocus={(e) => e.target.select()}
                          sx={hideNumberInputSpinners}
                          required
                          disabled={isSubmitting || success}
                          helperText="Additional wage when this rule applies"
                        />
                      </Grid>

                      {/* Third row: Remark */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Remark"
                          placeholder="Short description of this rule"
                          value={rule.remark}
                          onChange={(e) =>
                            handleRuleChange(index, 'remark', e.target.value)
                          }
                          disabled={isSubmitting || success}
                          helperText="Optional description to explain this rule's purpose"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            sx={{ color: '#ffffff' }}
            disabled={isSubmitting || success || !tagName.trim()}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}>
            {isSubmitting ? 'Creating...' : 'Create Tag'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

CreateTagModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onTagCreated: PropTypes.func,
};

export default CreateTagModal;
